/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  InputNumber,
  Tooltip,
  InputNumberProps,
  message,
} from 'antd';
import { Formik, FormikHelpers } from 'formik';
import React, { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dictionaries } from '../../interfaces';
import { GeneralApi, ModelApi, OptionApi, ValuationApi } from '../../apis';
import * as Yup from 'yup';
import { QuerySearch } from '../form/query-search';
import { parseNumber } from './vin';
import { PLATE_RE } from './register-no';

export const CYR_NAME = /^[А-Яа-я|0-9|Үү|Өө|Ёё|\\s|.|-]{2,255}$/;
export const LATIN_NAME = /^[A-Za-z|0-9|\\s|.|-]{2,255}$/;

export const BRANCH_CYR_NAME = /^[А-Яа-яҮүӨөЁё0-9\s.-]{2,255}$/;
export const BRANCH_LATIN_NAME = /^[A-Za-z0-9\s.-]{2,255}$/;

export const REG_NO = /^[\p{sc=Cyrillic}]{2}\d{8}$/u;
export const VIN_REGEX = /^[A-HJ-NPR-Za-hj-npr-z0-9]{9,17}$/;
export const ODO_MAX = 2000000000000;

const CURR = new Date().getFullYear();

// LICENSED,
// UNLICENSED
export const formSchema = (values: any) =>
  Yup.object().shape({
    condition: Yup.string().required('Заавал сонгоно уу'),


    vin: Yup.string()
      .trim()
      .when('condition', {
        is: 'UNLICENSED',
        then: (s) =>
          s
            .required('Дугаар аваагүй бол VIN-ийг заавал оруулна.')
            .matches(VIN_REGEX, 'VIN буруу форматтай. Жишээ: GWS00000000000000'),
        otherwise: (s) =>
          s
            .notRequired()
            .test('vin-optional', 'VIN буруу форматтай.', (v) => !v || VIN_REGEX.test(v)),
      }),

    plateNumber: Yup.string()
      .trim()
      .transform((v) => (v ? v.toUpperCase().replace(/\s+/g, '') : v))
      .when('condition', {
        is: 'LICENSED',
        then: (s) =>
          s
            .required('Дугаар авсан бол улсын дугаарыг заавал оруулна.')
            .matches(PLATE_RE, 'Формат: эхний 4 нь тоо, сүүлийн 3 нь крилл том үсэг (ж: 5929УНС)'),
        otherwise: (s) =>
          // оруулаагүй бол зөвшөөрнө, харин оруулбал regex шалгана
          s
            .notRequired()
            .test('vin-optional', 'Улсын дугаар буруу форматтай.', (v) => !v || VIN_REGEX.test(v)),
      }),

    //  plateNumber: Yup.string()
    // .transform((v) => (v ? v.toUpperCase().replace(/\s+/g, '') : v))
    // .matches(PLATE_RE, 'Формат: эхний 4 нь тоо, сүүлийн 3 нь крилл том үсэг (ж: 5929УНС)')
    // .required('Заавал оруулах'),

    registerNo: Yup.string()
      .transform((v) => (v ? v.toUpperCase().replace(/\s/g, '') : v))
      .matches(REG_NO, 'Формат: 2 крилл үсэг + 8 тоо (ж: УБ12345678)'),

    fullName: Yup.string()
      .trim()
      .matches(CYR_NAME, 'Зөвхөн крилл үсэг (зай, зураас) ашиглана уу')
      .required('Овог нэр заавал оруулах'),

    valuateType: Yup.mixed<'CUSTOM' | 'SYSTEM'>()
      .oneOf(['CUSTOM', 'SYSTEM'])
      .required('Заавал сонгоно уу'),

    odo: Yup.number()
      .typeError('Явсан км-ийг тоогоор оруулна уу (ж: 251250).')
      .integer('Явсан км бүхэл тоо байх ёстой.')
      .min(1, 'Явсан км 0-ээс бага байж болохгүй.')
      .max(ODO_MAX, `Явсан км ${ODO_MAX.toLocaleString()} км-ээс ихгүй байх ёстой.`)
      .required('Явсан км-ийг заавал оруулна уу.'),

    carBrandId: Yup.string().required('Заавал сонгоно уу'),
    carModelId: Yup.string().required('Заавал сонгоно уу'),

    engineCapacity: Yup.number().min(0, '0-с их байх ёстой').required('Заавал оруулах'),

    transmission: Yup.mixed<'AUTO' | 'MANUAL'>()
      .oneOf(['AUTO', 'MANUAL'])
      .required('Заавал сонгоно уу'),

    steering: Yup.mixed<'LEFT' | 'RIGHT'>().oneOf(['LEFT', 'RIGHT']).required('Заавал сонгоно уу'),

    engineType: Yup.string().required('Заавал сонгоно уу'),

    drivetrain: Yup.string().required('Заавал сонгоно уу'),
    exteriorColor: Yup.string().required('Заавал сонгоно уу'),
    interiorColor: Yup.string().required('Заавал сонгоно уу'),

    manufacturedYear: Yup.number()
      .typeError('Он буруу')
      .required('Заавал оруулах')
      .max(CURR, 'Ирээдүйн он')
      .test('lte-imported', 'Үйлдвэрлэсэн он нь импортолсон оноос их байж болохгүй', function (v) {
        const { importedYear } = this.parent;
        if (!v || !importedYear) return true;
        return v <= importedYear; // ≤
      }),

    importedYear: Yup.number()
      .typeError('Он буруу')
      .required('Заавал оруулах')
      .max(CURR, 'Ирээдүйн он')
      .test(
        'gte-manufactured',
        'Импортолсон он нь үйлдвэрлэсэн оноос бага байж болохгүй',
        function (v) {
          const { manufacturedYear } = this.parent;
          if (!v || !manufacturedYear) return true;
          return v >= manufacturedYear; // ≥
        }
      ),

    price: Yup.number().min(0, '0 -с их байх шаардаглгатай').required('Заавал оруулах'),
  });

type FormDataType = {
  valuateType: 'CUSTOM' | 'SYSTEM' | undefined;
  odo: number | undefined;
  carBrandId: string | undefined;
  carModelId: string | undefined;
  engineCapacity: number | undefined;
  transmission: 'AUTO' | 'MANUAL' | undefined;
  condition: 'UNLICENSED' | 'LICENSED' | undefined;
  steering: 'LEFT' | 'RIGHT' | undefined;
  engineType: undefined;
  drivetrain: 'FWD' | 'RWD' | 'AWD' | '4WD' | undefined;
  exteriorColor: string | undefined;
  interiorColor: string | undefined;
  manufacturedYear: number | undefined;
  importedYear: number | undefined;

  registerNo: string | undefined;
  fullName: string | undefined;

  price: number | undefined;
  vin: string | undefined;
  plateNumber: string | undefined;

  optionId?: string;
};

type Props = {
  setAction: React.Dispatch<
    React.SetStateAction<{
      loading: boolean;
      payload: any;
      percent?: any;
      found?: 'ADDITIONAL_DATA' | 'IS_ADDITIONAL_DATA' | boolean;
    }>
  >;
  payload?: any;
};

const ValuationCustomForm = memo(({ setAction, payload }: Props) => {
  const [general, setGeneral] = useState<Dictionaries>();
  const [messageApi, contextHolderMessage] = message.useMessage();
  const [images, setImages] = useState<string[]>([]);
  const loadData = useCallback(async () => {
    const res = await GeneralApi.init();
    setGeneral(res);
  }, []);
  const [loading, setLoading] = useState(false);

  const initialValues: FormDataType | any = {
    valuateType: 'CUSTOM',
    registerNo: 'СЧ98126688',
    fullName: 'Түвшинжаргал',
    odo: 250000,
    carBrandId: '66340c0a-824b-43b8-9c7c-baf41c95beb8',
    carModelId: '24027476-b4f0-4ced-a3c0-5bd3f561c7cb',
    engineCapacity: '5.0',
    transmission: 'MANUAL',
    condition: 'LICENSED',
    steering: 'RIGHT',
    engineType: 'GASOLINE',
    drivetrain: 'FWD',
    exteriorColor: 'BLUE',
    interiorColor: 'BLACK',
    manufacturedYear: '2006',
    importedYear: '2024',
    price: 45000000,
    plateNumber: '0008ЭАХ',
    optionId: 'a19ede5c-222f-40e1-804e-b15f42122fd6',
    ...(payload || {}),
  };

  const getOption = useCallback((value?: { code?: string; name: string; id: string }[] | any) => {
    if (!value) {
      return [];
    }

    return value.map((item: any) => ({
      label: item.name,
      value: item.code ?? item.id,
    }));
  }, []);

  const onSubmit = useCallback(
    async (values: FormDataType) => {
      setAction({
        loading: true,
        payload: undefined,
      });

      try {
        setLoading(true);
        const res = await ValuationApi.valuate(values);

        setAction({
          loading: false,
          payload: res,
          found: 'IS_ADDITIONAL_DATA',
        });
        setLoading(false);
      } catch (e: any) {
        messageApi.open({
          type: 'error',
          content: e?.message,
        });
        setLoading(false);
        setAction({
          loading: false,
          payload: undefined,
          found: false,
        });
      }
    },
    [messageApi, setAction]
  );

  const formatter: InputNumberProps<number>['formatter'] = (value: any) => {
    const [start, end] = `${value}`.split('.') || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `₮ ${end ? `${v}.${end}` : `${v}`}`;
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // if (!general) return <></>;

  return (
    <div>
      {contextHolderMessage}
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={(values: FormDataType, actions: FormikHelpers<FormDataType>) => onSubmit(values)}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched, validateField, submitCount }) => {
          console.log('Errors :', errors);

          return (
            <Container>
              <Form
                layout="vertical"
                // form sm:w-full md:w-full lg:w-[600px] xl:w-[600px] 2xl-[800px] w-[600px]
                // className="form w-[400px]"
                style={{ maxWidth: '400px' }}
                onFinish={handleSubmit}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Овог, нэр"
                      validateStatus={errors.fullName && touched.fullName ? 'error' : ''}
                      help={errors.fullName && touched.fullName ? errors.fullName : ''}
                    >
                      <Input
                        name="fullName"
                        status={errors.fullName && touched.fullName ? 'error' : ''}
                        value={values.fullName}
                        onChange={(e) => setFieldValue('fullName', e.target.value)}
                        placeholder="Зээл хүсэгчийн овог, нэр"
                        onBlur={() => {
                          if (errors.fullName) {
                            messageApi.error(errors.fullName);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Регистрийн дугаар"
                      validateStatus={errors.registerNo && touched.registerNo ? 'error' : ''}
                      help={errors.registerNo && touched.registerNo ? errors.registerNo : ''}
                    >
                      <Input
                        name="registerNo"
                        value={(values as any).registerNo}
                        onChange={(e) => setFieldValue('registerNo', e.target.value)}
                        placeholder="Регистрийн дугаар"
                        onBlur={() => {
                          if (errors.registerNo) {
                            messageApi.error(errors.registerNo);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={values.condition ? 12 : 24}>
                    <Form.Item
                      label="Нөхцөл"
                      validateStatus={errors.condition && touched.condition ? 'error' : ''}
                      help={errors.condition && touched.condition ? errors.condition : ''}
                    >
                      <Select
                        placeholder="Нөхцөл"
                        options={getOption(general?.vehicleConditions)}
                        value={values.condition}
                        onChange={(val) => setFieldValue('condition', val)}
                      />
                    </Form.Item>
                  </Col>

                  {values.condition === 'LICENSED' && (
                    <Col span={12}>
                      <Form.Item
                        label="Улсын дугаар"
                        validateStatus={errors.plateNumber && touched.plateNumber ? 'error' : ''}
                        help={errors.plateNumber && touched.plateNumber ? errors.plateNumber : ''}
                      >
                        <Input
                          name="plateNumber"
                          status={errors.plateNumber && touched.plateNumber ? 'error' : ''}
                          value={values.plateNumber}
                          onChange={(e) => setFieldValue('plateNumber', e.target.value)}
                          placeholder="Зээл хүсэгчийн овог, нэр"
                          onBlur={() => {
                            if (errors.plateNumber) {
                              messageApi.error(errors.plateNumber);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {values.condition === 'UNLICENSED' && (
                    <Col span={12}>
                      <Form.Item
                        label="Арлын дугаар"
                        validateStatus={errors.vin && touched.vin ? 'error' : ''}
                        help={errors.vin && touched.vin ? errors.vin : ''}
                      >
                        <Input
                          name="vin"
                          value={(values as any).vin}
                          onChange={(e) => setFieldValue('vin', e.target.value)}
                          placeholder="Арлын дугаар"
                          onBlur={() => {
                            if (errors.vin) {
                              messageApi.error(errors.vin);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Брэнд"
                      validateStatus={errors.carBrandId && touched.carBrandId ? 'error' : ''}
                      help={errors.carBrandId && touched.carBrandId ? errors.carBrandId : ''}
                    >
                      <Select
                        placeholder="Брэнд сонгох"
                        options={getOption(general?.carBrands || [])}
                        value={values.carBrandId}
                        onChange={(val) => {
                          setFieldValue('carModelId', undefined);
                          setFieldValue('carBrandId', val);
                          setFieldValue('optionId', undefined);
                        }}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? ('' as any)).toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Загвар"
                      validateStatus={errors.carModelId && touched.carModelId ? 'error' : ''}
                      help={errors.carModelId && touched.carModelId ? errors.carModelId : ''}
                    >
                      <QuerySearch
                        value={values.carModelId!}
                        onChange={(e) => {
                          setFieldValue('carModelId', e);
                          setFieldValue('optionId', undefined);
                        }}
                        placeholder="Загвар сонгох"
                        showSearch
                        payload={{ carBrandId: values.carBrandId }}
                        loadData={ModelApi.select}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Моторын хэмжээ"
                      validateStatus={
                        errors.engineCapacity && touched.engineCapacity ? 'error' : ''
                      }
                      help={
                        errors.engineCapacity && touched.engineCapacity ? errors.engineCapacity : ''
                      }
                    >
                      <Select
                        placeholder="Моторын хэмжээ"
                        options={getOption(general?.engineCapacities)}
                        value={values.engineCapacity}
                        onChange={(val) => setFieldValue('engineCapacity', val)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Хурдны хайрцаг"
                      validateStatus={errors.transmission && touched.transmission ? 'error' : ''}
                      help={errors.transmission && touched.transmission ? errors.transmission : ''}
                    >
                      <Select
                        placeholder="Хурдны хайрцаг"
                        options={getOption(general?.transmissionTypes)}
                        value={values.transmission}
                        onChange={(val) => setFieldValue('transmission', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  {/*  */}

                  <Col span={24}>
                    <Form.Item
                      label="Хүрд"
                      validateStatus={errors.steering && touched.steering ? 'error' : ''}
                      help={errors.steering && touched.steering ? errors.steering : ''}
                    >
                      <Select
                        placeholder="Хүрд"
                        options={getOption(general?.steeringTypes)}
                        value={values.steering}
                        onChange={(val) => setFieldValue('steering', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Хөдөлгүүр"
                      validateStatus={errors.engineType && touched.engineType ? 'error' : ''}
                      help={errors.engineType && touched.engineType ? errors.engineType : ''}
                    >
                      <Select
                        placeholder="Хөдөлгүүр"
                        options={getOption(general?.engineTypes)}
                        value={values.engineType}
                        onChange={(val) => setFieldValue('engineType', val)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Хөтлөгч"
                      validateStatus={errors.drivetrain && touched.drivetrain ? 'error' : ''}
                      help={errors.drivetrain && touched.drivetrain ? errors.drivetrain : ''}
                    >
                      <Select
                        placeholder="Хөтлөгч"
                        options={getOption(general?.drivetrain)}
                        value={values.drivetrain}
                        onChange={(val) => setFieldValue('drivetrain', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Гадна өнгө"
                      validateStatus={errors.exteriorColor && touched.exteriorColor ? 'error' : ''}
                      help={
                        errors.exteriorColor && touched.exteriorColor ? errors.exteriorColor : ''
                      }
                    >
                      <Select
                        placeholder="Өнгө"
                        options={getOption(general?.exteriorColors)}
                        value={values.exteriorColor}
                        onChange={(val) => setFieldValue('exteriorColor', val)}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Дотор өнгө"
                      validateStatus={errors.interiorColor && touched.interiorColor ? 'error' : ''}
                      help={
                        errors.interiorColor && touched.interiorColor ? errors.interiorColor : ''
                      }
                    >
                      <Select
                        placeholder="Дотор өнгө"
                        options={getOption(general?.interiorColors)}
                        value={values.interiorColor}
                        onChange={(val) => setFieldValue('interiorColor', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Үйлдвэрлэсэн он"
                      validateStatus={
                        errors.manufacturedYear && touched.manufacturedYear ? 'error' : ''
                      }
                      help={
                        errors.manufacturedYear && touched.manufacturedYear
                          ? errors.manufacturedYear
                          : ''
                      }
                    >
                      <Select
                        placeholder="Он сонгох"
                        options={general?.yearsFrom2000.map((e) => ({ label: e, value: e }))}
                        value={values.manufacturedYear}
                        onChange={(val) => setFieldValue('manufacturedYear', val)}
                        onBlur={() => {
                          if (errors.manufacturedYear) {
                            messageApi.error(errors.manufacturedYear);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Орж ирсэн он дээд"
                      validateStatus={errors.importedYear && touched.importedYear ? 'error' : ''}
                      help={errors.importedYear && touched.importedYear ? errors.importedYear : ''}
                    >
                      <Select
                        placeholder="Он сонгох"
                        options={general?.yearsFrom2000.map((e) => ({ label: e, value: e }))}
                        value={values.importedYear}
                        onChange={(val) => setFieldValue('importedYear', val)}
                        onBlur={() => {
                          if (errors.importedYear) {
                            messageApi.error(errors.importedYear);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Явсан км"
                      validateStatus={errors.odo && touched.odo ? 'error' : ''}
                      help={errors.odo && touched.odo ? errors.odo : ''}
                    >
                      <InputNumber
                        placeholder="Явсан км"
                        value={values.odo}
                        onChange={(val) => setFieldValue('odo', val)}
                        style={{ width: '100%' }}
                        suffix={<Tooltip title="Extra information">КМ</Tooltip>}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Үнэ"
                      validateStatus={errors.price && touched.price ? 'error' : ''}
                      help={errors.price && touched.price ? errors.price : ''}
                    >
                      <InputNumber
                        placeholder="Үнэ"
                        value={values.price}
                        onChange={(val) => setFieldValue('price', val)}
                        style={{ width: '100%' }}
                        formatter={formatter}
                        parser={parseNumber}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Тоноглол"
                      layout="vertical"
                      validateStatus={errors.optionId && touched.optionId ? 'error' : ''}
                      help={errors.optionId && touched.optionId ? errors.optionId : ('' as any)}
                    >
                      <QuerySearch
                        value={values.optionId!}
                        onChange={(e) => setFieldValue('optionId', e)}
                        placeholder="Тоноглол"
                        showSearch
                        loadData={OptionApi.select(values.carModelId!) as any}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  className="primary-btn"
                  type="primary"
                  onClick={() => handleSubmit()}
                  loading={loading}
                >
                  Үнэлгээ хийх
                </Button>
              </Form>
            </Container>
          );
        }}
      </Formik>
    </div>
  );
});

const Container = styled.div`
  /* border: solid 1px #ffefee; */
  /* border-radius: 8px; */
  padding: 16px;

  .form {
    /* width: 100%; */
  }
`;

ValuationCustomForm.displayName = 'ValuationCustomForm';

export { ValuationCustomForm };
